import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import {http} from 'hiya-common/src/utils/axios'
import {useSelector} from 'react-redux'

/** 处罚类型：掉到 60 以下 / 掉到 70 以下 / 恢复 */
export type FemaleHealthScorePunishNoticeKind =
  | 'below_60'
  | 'warn_70'
  | 'daily_recovery'

export interface FemaleHealthScorePunishNoticeSt {
  kind: FemaleHealthScorePunishNoticeKind
  /** 处罚文案 */
  message: string
  /** 触发处罚的时间戳（秒） */
  ct_unix: number
}

export interface FemaleHealthScoreBreakdownSt {
  /** 不礼貌扣分（一般为负） */
  impolite_deduct: number
  /** 索要礼物扣分 */
  gift_deduct: number
  /** 敷衍扣分 */
  unmeaning_deduct: number
}

/** 健康分页接口 data，与后端字段对齐 */
export interface FemaleHealthScorePageSt {
  /** 本周一的日期 yyyy-mm-dd */
  week_start_date: string
  health_score: number
  punish_notices: FemaleHealthScorePunishNoticeSt[]
  score_breakdown: FemaleHealthScoreBreakdownSt
  /** N / B / A / S / SS */
  user_level: string
}

interface HealthScoreState {
  active: FemaleHealthScorePageSt
}

const emptyBreakdown: FemaleHealthScoreBreakdownSt = {
  impolite_deduct: 0,
  gift_deduct: 0,
  unmeaning_deduct: 0,
}

const initialState: HealthScoreState = {
  active: {
    week_start_date: '',
    health_score: 0,
    punish_notices: [],
    score_breakdown: {...emptyBreakdown},
    user_level: 'N',
  },
}

/**
 * 拉取健康分页数据。若后端 path 不一致，仅改此常量即可。
 */
export const FETCH_FEMALE_HEALTH_SCORE_PAGE =
  '/live/hiya_socialize/fetch_health_score_page'

export const fetch_female_health_score_page = createAsyncThunk(
  'HealthScore/fetch_female_health_score_page',
  async (params: Record<string, never>, store: any) => {
    const resp = await http.post(
      FETCH_FEMALE_HEALTH_SCORE_PAGE,
      {...params},
      {store, xcErrorToast: true}
    )
    return (resp?.data?.data || {}) as Partial<FemaleHealthScorePageSt>
  }
)

export const HealthScoreSlice = createSlice({
  name: 'HealthScore',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      fetch_female_health_score_page.fulfilled,
      (state, action) => {
        const p = action.payload || {}
        const mergedBreakdown = {
          ...emptyBreakdown,
          ...state.active.score_breakdown,
          ...(p.score_breakdown || {}),
        }
        Object.assign(state.active, {
          ...p,
          score_breakdown: mergedBreakdown,
          punish_notices: Array.isArray(p.punish_notices)
            ? p.punish_notices
            : state.active.punish_notices,
        })
      }
    )
  },
})

export default HealthScoreSlice.reducer
export const HealthScoreActions = HealthScoreSlice.actions
export const useHealthScoreState = () =>
  useSelector((store: any) => store.HealthScore as HealthScoreState)
